import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { resetPassword } from '../../redux/actions/forgetPasswordAction';
import { resetForgetPasswordState } from '../../redux/slices/forgetPasswordSlice';
import { useDarkMode } from '../../App';
import { motion } from 'framer-motion';
import { FiLock, FiEye, FiEyeOff, FiArrowLeft, FiSmartphone, FiShield, FiGlobe } from 'react-icons/fi';
import '../AuthShared.css';

const LanguageSwitcher = () => {
    const { i18n } = useTranslation('global');
    const isAR = i18n.language === 'ar';

    const toggleLanguage = () => {
        const newLang = isAR ? 'en' : 'ar';
        i18n.changeLanguage(newLang);
        localStorage.setItem('lang', newLang);
    };

    return (
        <motion.button
            className="auth-lang-switcher"
            onClick={toggleLanguage}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
        >
            <FiGlobe />
            <span>{isAR ? 'English' : 'العربية'}</span>
        </motion.button>
    );
};

const ResetPassword = () => {
  const { t, i18n } = useTranslation('global');
  const { isDarkMode } = useDarkMode();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const forgetPasswordState = useSelector((state) => state.forgetPassword);
  const { isLoading, success, error, data } = forgetPasswordState || {};

  const [email, setEmail] = useState(location.state?.email || '');
  const [formData, setFormData] = useState({
    password: '',
    password_confirmation: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = t('resetPassword.title', 'Reset Password');
  }, [t]);

  useEffect(() => {
    if (success) {
      toast.success(data?.message || t('resetPassword.successMessage', 'Password reset successfully!'));
      dispatch(resetForgetPasswordState());
      setTimeout(() => navigate('/login'), 1000);
    }
  }, [success, data, t, navigate, dispatch]);

  useEffect(() => {
    if (error) {
      toast(error);
      dispatch(resetForgetPasswordState());
    }
  }, [error, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(resetForgetPasswordState());
    };
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!email) {
      toast(t('resetPassword.emailRequired', 'Email is required'));
      return false;
    }
    if (!formData.password) {
      toast(t('resetPassword.passwordRequired', 'Password is required'));
      return false;
    }
    if (formData.password.length < 8) {
      toast(t('resetPassword.passwordMinLength', 'Password must be at least 8 characters'));
      return false;
    }
    if (formData.password !== formData.password_confirmation) {
      toast(t('resetPassword.passwordMismatch', 'Passwords do not match'));
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (validateForm()) {
      dispatch(resetPassword({ 
        email, 
        password: formData.password, 
        password_confirmation: formData.password_confirmation,
        lang: i18n.language 
      }));
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.6, staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.5 } }
  };

  return (
    <div className={`auth-page ${isDarkMode ? 'dark' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="auth-bg-overlay"></div>
      <div className="auth-blobs">
        <div className="auth-blob"></div>
        <div className="auth-blob"></div>
        <div className="auth-blob"></div>
      </div>

      <LanguageSwitcher />

      <motion.div 
        className="auth-container"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <motion.div className="auth-card" variants={itemVariants}>
          <div className="auth-brand">
            <motion.div className="auth-brand-circle" whileHover={{ scale: 1.05, rotate: 5 }}>
              <FiShield style={{ fontSize: '24px', color: '#fff' }} />
            </motion.div>
            <h1>{t('resetPassword.brandTitle', 'Security Key')}</h1>
          </div>

          <div className="auth-header">
            <h2>{t('resetPassword.title', 'Update Password')}</h2>
            <p>{t('resetPassword.subtitle', 'Secure your ecosystem with a new password')}</p>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="auth-input-group">
              <label><FiLock /> {t('resetPassword.password', 'New Password')}</label>
              <div className="auth-input-wrapper" style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className={submitted && !formData.password ? 'error' : ''}
                  style={{ width: '100%' }}
                />
                <button 
                  type="button" 
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ position: 'absolute', right: '15px', background: 'none', border: 'none', color: '#666', cursor: 'pointer' }}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </div>

            <div className="auth-input-group">
              <label><FiLock /> {t('resetPassword.confirmPassword', 'Confirm Password')}</label>
              <div className="auth-input-wrapper" style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="password_confirmation"
                  value={formData.password_confirmation}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className={submitted && formData.password !== formData.password_confirmation ? 'error' : ''}
                  style={{ width: '100%' }}
                />
                <button 
                  type="button" 
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{ position: 'absolute', right: '15px', background: 'none', border: 'none', color: '#666', cursor: 'pointer' }}
                >
                  {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </div>

            <motion.button 
              type="submit" 
              className="auth-submit-btn" 
              disabled={isLoading}
              whileTap={{ scale: 0.98 }}
            >
              {isLoading ? <span className="auth-spinner"></span> : t('resetPassword.resetButton', 'Update Password')}
            </motion.button>
          </form>

          <div className="auth-footer">
            <button className="auth-back-btn" onClick={() => navigate('/login')}>
              <FiArrowLeft style={{ verticalAlign: 'middle', marginRight: '5px' }} />
              {t('resetPassword.backToLogin', 'Cancel & Return')}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ResetPassword;

